import { useEffect, useState } from 'react';
import { AlertDialog, Button, Callout, DateField, Modal, TextField } from '@humanforce/mango-ui-react';
import { DetailPair } from './components/SectionShell';
import { correctWorkerLeaveOpening, exactLeaveHours, postWorkerLeaveOpening, type WorkerLeaveAccount } from './workerDirectoryApi';

type Mode = 'open' | 'correct';
type Field = 'amount' | 'asOf' | 'covered' | 'reason' | 'source';
const blankErrors = (): Record<Field, string> => ({ amount: '', asOf: '', covered: '', reason: '', source: '' });
const validHours = (value: string) => /^(?:0|[1-9]\d{0,13})(?:\.\d{1,4})?$/.test(value);

export function WorkerLeaveOpeningDialog({ personId, account, mode, onHide, onPosted }: {
  personId: string; account: WorkerLeaveAccount | null; mode: Mode | null;
  onHide: () => void; onPosted: () => Promise<void>;
}) {
  const [amount, setAmount] = useState('');
  const [asOf, setAsOf] = useState('');
  const [covered, setCovered] = useState('');
  const [reason, setReason] = useState('');
  const [source, setSource] = useState('');
  const [operationReference, setOperationReference] = useState(() => crypto.randomUUID());
  const [errors, setErrors] = useState(blankErrors);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setAmount(mode === 'correct' ? exactLeaveHours(account?.opening?.currentOpeningHoursExact,
      account?.opening?.currentOpeningHours) ?? '' : '');
    setAsOf(mode === 'correct' ? account?.opening?.balanceAsOfDate ?? '' : '');
    setCovered(mode === 'correct' ? account?.opening?.accrualCoveredThroughDate ?? '' : '');
    setReason(''); setSource(''); setErrors(blankErrors()); setError(null); setConflict(false); setConfirm(false); setBusy(false);
    setOperationReference(crypto.randomUUID());
  }, [personId, account?.id, account?.accountRowVersion, mode]);

  function change(update: () => void) {
    update(); setErrors(blankErrors()); setError(null); setConflict(false); setOperationReference(crypto.randomUUID());
  }

  function review() {
    const next = blankErrors();
    if (!validHours(amount)) next.amount = 'Enter non-negative hours with at most four decimal places.';
    if (!asOf) next.asOf = 'Enter the balance as-of date.';
    if (!covered) next.covered = 'Enter the historical accrual and consumption covered-through date.';
    if (covered && asOf && covered > asOf) next.covered = 'Coverage cannot end after the balance as-of date.';
    if (!reason.trim()) next.reason = 'Enter the reason for this opening or correction.';
    if (reason.trim().length > 1000) next.reason = 'Reason must be at most 1000 characters.';
    if (!source.trim()) next.source = 'Enter a reference to independently retrievable supporting evidence.';
    if (source.trim().length > 200) next.source = 'Source reference must be at most 200 characters.';
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;
    setConfirm(true);
  }

  async function post() {
    if (!account || !mode) return;
    setBusy(true);
    const common = {
      balanceAsOfDate: asOf, accrualCoveredThroughDate: covered,
      reason: reason.trim(), sourceReference: source.trim(), operationReference,
      expectedAccountRowVersion: account.accountRowVersion,
    };
    const result = mode === 'open'
      ? await postWorkerLeaveOpening(personId, account.id, { ...common, openingBalanceHours: amount })
      : await correctWorkerLeaveOpening(personId, account.id, {
        ...common, openingMovementId: account.opening?.currentMovementId ?? '', replacementBalanceHours: amount,
      });
    setBusy(false); setConfirm(false);
    if (!result.ok) {
      setError(result.message ?? 'Could not post the opening. Reload the account and retry.');
      setConflict(result.status === 409);
      return;
    }
    await onPosted();
    onHide();
  }

  const correction = mode === 'correct';
  return <>
    <Modal visible={mode !== null} header={correction ? 'Correct Opening Balance' : 'Enter Opening Balance'}
      size="large" onHide={() => { if (!busy) onHide(); }}>
      <div className="mgo-stack mgo-gap-l">
        {account && <div className="mgo-stack mgo-gap-s" aria-label="Selected Leave account">
          <DetailPair label="Leave Type" value={account.leaveTypeName} />
          <DetailPair label="Current stored balance (hours)" value={exactLeaveHours(account.currentBalanceHoursExact,
            account.currentBalanceHours) ?? '—'} />
          {correction && <DetailPair label="Opening being corrected" value={account.opening?.currentMovementId ?? '—'} />}
        </div>}
        <Callout tone="caution" title={correction ? 'Correction preserves history' : 'Opening is a ledger posting'}>
          {correction
            ? 'The original opening remains in history. This posts a compensating reversal and a new evidenced opening. Finalised Pay Runs are not recalculated; payroll impacts need the controlled payroll correction process.'
            : 'The posted opening becomes part of the account balance and immutable movement history. The source reference must identify evidence that can be retrieved independently.'}
        </Callout>
        <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
          <TextField label={correction ? 'Replacement opening balance (hours)' : 'Opening balance (hours)'}
            value={amount} required onChange={(event) => change(() => setAmount(event.target.value))}
            status={errors.amount ? 'critical' : undefined} statusText={errors.amount || undefined} />
          <DateField label="Balance as-of date" dateFormat="ymd" value={asOf} required fullWidth
            onChange={(value) => change(() => setAsOf(value))} onClear={() => change(() => setAsOf(''))}
            status={errors.asOf ? 'critical' : undefined} statusText={errors.asOf || undefined} />
        </div>
        <DateField label="Historical accrual and consumption covered through" dateFormat="ymd"
          value={covered} required fullWidth onChange={(value) => change(() => setCovered(value))}
          onClear={() => change(() => setCovered(''))}
          status={errors.covered ? 'critical' : undefined} statusText={errors.covered || undefined} />
        <TextField label={correction ? 'Correction reason' : 'Opening reason'} value={reason} required
          onChange={(event) => change(() => setReason(event.target.value))}
          status={errors.reason ? 'critical' : undefined} statusText={errors.reason || undefined} />
        <TextField label="Source reference" value={source} required
          helpText="Use a reference that identifies independently retrievable supporting evidence."
          onChange={(event) => change(() => setSource(event.target.value))}
          status={errors.source ? 'critical' : undefined} statusText={errors.source || undefined} />
        {error && <div role="alert"><Callout tone="critical" title="Opening not posted">{error}</Callout></div>}
        <div className="mgo-cluster mgo-gap-s mgo-justify-end">
          {conflict && <Button label="Reload Leave account" variant="secondary" disabled={busy}
            onClick={() => void onPosted().then(onHide)} />}
          <Button label="Cancel" variant="tertiary" disabled={busy} onClick={onHide} />
          <Button label="Review posting" variant="primary" disabled={busy} onClick={review} />
        </div>
      </div>
    </Modal>
    <AlertDialog visible={confirm} header={correction ? 'Post opening correction?' : 'Post opening balance?'}
      severity="warning" confirmLabel={correction ? 'Post correction' : 'Post opening'}
      confirmLoading={busy} onConfirm={() => void post()} onCancel={() => { if (!busy) setConfirm(false); }}>
      {`${correction ? 'Replace the evidenced opening with' : 'Post'} ${amount} hours as of ${asOf}, covering historical accrual and consumption through ${covered}. Source: ${source.trim()}. This creates immutable audit history.`}
    </AlertDialog>
  </>;
}
